import { z } from "zod";

import { emailSchema, nameSchema } from "./common";

// Base schema for claim data with string inputs
const claimFormSchema = z
  .object({
    // For physical items
    amount: z.string().optional(), // For financial assistance (in dollars)
    church: z.string().optional(),

    city: z.string().optional(),

    country: z.string().optional(),

    customResidenceArea: z.string().optional(),

    email: emailSchema,

    // Donor's estimated value for in-kind (physical item) donations; only donor can legally estimate
    estimatedValue: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val ||
          val.trim() === "" ||
          (parseFloat(val) >= 0 && !Number.isNaN(parseFloat(val))),
        { message: "Estimated value must be a non-negative number" },
      ),

    firstName: nameSchema,

    frequency: z.enum(["immediate", "daily", "weekly"]).optional(),
    lastName: nameSchema,

    needId: z.string().optional(),

    // For people needed
    peopleCount: z.string().optional(),
    // Optional in form, required for database
    phone: z.string(),
    // Phone is now required for claiming needs
    quantity: z.string().optional(),
    // For physical items
    residenceAreaId: z.string().optional(),
    serviceAreas: z.array(z.string()).optional(),
    state: z.string().optional(),
    streetAddress: z.string().optional(),
    subscribe: z.boolean().optional(),
    zipCode: z.string().optional(),
  })
  .refine(
    (data) => {
      // If user is subscribing to future needs, either residenceAreaId or customResidenceArea is required
      if (data.subscribe) {
        return (
          Boolean(data.residenceAreaId && data.residenceAreaId.trim() !== "") ||
          Boolean(
            data.residenceAreaId === "other" &&
              data.customResidenceArea &&
              data.customResidenceArea.trim() !== "",
          )
        );
      }

      return true;
    },
    {
      message: "Area of residence is required when subscribing to future needs",
      path: ["residenceAreaId"],
    },
  )
  .refine(
    (data) => {
      if (!data.subscribe) return true;
      const fields = [data.streetAddress, data.city, data.state, data.zipCode];

      return fields.every((f) => Boolean(f && f.trim() !== ""));
    },
    {
      message:
        "Physical address (street, city, state, ZIP) is required when subscribing to future needs",
      path: ["streetAddress"],
    },
  );

// Transformed schema for server actions (with number conversions)
export const claimSchema = claimFormSchema.transform((data) => ({
  ...data,

  // Convert string inputs to numbers
  amount: data.amount ? parseFloat(data.amount) : undefined,
  estimatedValue: data.estimatedValue
    ? parseFloat(data.estimatedValue)
    : undefined,
  peopleCount: data.peopleCount ? parseInt(data.peopleCount, 10) : undefined,
  quantity: data.quantity ? parseInt(data.quantity, 10) : undefined,
}));

// Schema factory for forms with max quantity validation
export const createClaimSchema = (maxQuantity?: number) => {
  if (!maxQuantity) return claimFormSchema;

  return claimFormSchema.safeExtend({
    quantity: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val || (parseInt(val, 10) >= 1 && parseInt(val, 10) <= maxQuantity),
        {
          message: "Quantity cannot exceed available items",
        },
      ),
  });
};

// Export types
export type ClaimFormData = z.infer<typeof claimFormSchema>;
// Parsed server-side type (all numeric fields converted to numbers)
export type ClaimParsedData = Omit<
  z.infer<typeof claimFormSchema>,
  "quantity" | "amount" | "peopleCount" | "estimatedValue"
> & {
  quantity?: number;
  amount?: number;
  peopleCount?: number;
  estimatedValue?: number;
};
